import { z } from "zod";

/**
 * What the Front desk role (migration 0080, T1) may see and do at one business: the owner's choice in
 * Settings, Front desk (decisions D58, D65, D68 of the Pilates studio shape). Stored in
 * `businesses.settings.frontDesk`, one boolean for each item. A missing key takes its start value
 * from `FRONT_DESK_DEFAULTS`, so a business that never opened the tab works as before.
 *
 * The front desk always books for a client, takes a booking off, checks people in, records a payment
 * at the desk and approves a new client; it always opens the Calendar and the client pages for these
 * jobs. The items below add to that. The API reads the same items (`frontDeskRules` in
 * `desk-permissions.ts`): an item that is off is refused on the server, not only hidden. The owner and
 * the admins are never limited by them, and staff permissions do not read them.
 */

/** The pages the owner can open to the front desk. */
export const frontDeskPageItems = ["homeNumbers", "classes", "classesEdit", "payments", "team"] as const;

/** The jobs on the client pages and the client list. */
export const frontDeskClientItems = [
  "adjustBalance",
  "freeze",
  "paymentRequests",
  "photo",
  "editClient",
  "addClients",
  "sendInvites",
  "messageClients",
  "exportClients",
] as const;

export const frontDeskItems = [...frontDeskPageItems, ...frontDeskClientItems] as const;
export type FrontDeskItem = (typeof frontDeskItems)[number];
export const frontDeskItemSchema = z.enum(frontDeskItems);

export type FrontDeskAccess = Record<FrontDeskItem, boolean>;

/**
 * The start values: what the front desk had before the setting (D58, D65, D68), and the Classes page
 * to read (D58 review: "some business might need classes, let it be shown by default").
 */
export const FRONT_DESK_DEFAULTS: FrontDeskAccess = {
  homeNumbers: false,
  classes: true,
  classesEdit: false,
  payments: true,
  team: false,
  adjustBalance: false,
  freeze: false,
  paymentRequests: false,
  photo: false,
  editClient: false,
  addClients: false,
  sendInvites: false,
  messageClients: false,
  exportClients: false,
};

/**
 * `settings.frontDesk` as stored and as sent: any of the items. A save merges the items it sends
 * into the stored ones (`updateBusinessProfile`), and every answer has all of them.
 */
export const frontDeskAccessSchema = z.object(
  Object.fromEntries(frontDeskItems.map((item) => [item, z.boolean()])) as Record<FrontDeskItem, z.ZodBoolean>,
).partial();

/**
 * Every item, from what is stored. Editing classes needs the Classes page: with the page off, the
 * edit item is off too, whatever is stored.
 */
export function frontDeskAccessOf(stored: Partial<FrontDeskAccess> | null | undefined): FrontDeskAccess {
  const access = { ...FRONT_DESK_DEFAULTS };
  for (const item of frontDeskItems) {
    const value = stored?.[item];
    if (typeof value === "boolean") access[item] = value;
  }
  if (!access.classes) access.classesEdit = false;
  return access;
}

/** The items that changed between two states, in the order of the list. */
export function changedFrontDeskItems(before: FrontDeskAccess, after: FrontDeskAccess): { turnedOn: FrontDeskItem[]; turnedOff: FrontDeskItem[] } {
  return {
    turnedOn: frontDeskItems.filter((item) => !before[item] && after[item]),
    turnedOff: frontDeskItems.filter((item) => before[item] && !after[item]),
  };
}
